"use client";

import {
  CenterFileModel,
  SiteTemplatePageSectionTypeWorkDefaultListItemSettingModel,
} from "@/Apis/ClinicApi";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { WorkSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeEighteenWorkSectionGenerated, {
  HomeEighteenWorkSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenWorkSection.generated";
import HomeElevenWorkSectionGenerated, {
  HomeElevenWorkSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenWorkSection.generated";
import HomeFourteenTeamSectionGenerated, {
  HomeFourteenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenTeamSection.generated";
import HomeSevenWorkSectionGenerated, {
  HomeSevenWorkSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenWorkSection.generated";
import HomeTenWorkSectionGenerated, {
  HomeTenWorkSectionGeneratedProps,
} from "../Sections/_generated/HomeTenWorkSection.generated";
import HomeTwelveWorkSectionGenerated, {
  HomeTwelveWorkSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveWorkSection.generated";
import HomeTwoWorkSectionGenerated, {
  HomeTwoWorkSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoWorkSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedWorkRenderer = (props: WorkSectionProps) => ReactElement;

const WorkAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateWorkAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getListItems(
  props: WorkSectionProps,
): SiteTemplatePageSectionTypeWorkDefaultListItemSettingModel[] {
  return props.lists.Default ?? [];
}

function getSectionImageUrl(props: WorkSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(props: WorkSectionProps, fallback: string): string {
  return props.setting.Image?.AltText ?? fallback;
}

function isImageLikeValue(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function getItemImageUrl(image?: CenterFileModel | null): string | undefined {
  return image?.Url ?? undefined;
}

function getItemVisual(
  item: SiteTemplatePageSectionTypeWorkDefaultListItemSettingModel | undefined,
  fallback?: string,
): string | undefined {
  if (isImageLikeValue(item?.Icon)) {
    return item?.Icon ?? undefined;
  }

  return getItemImageUrl(item?.Image) ?? fallback;
}

function getItemStepText(
  item: SiteTemplatePageSectionTypeWorkDefaultListItemSettingModel | undefined,
  index: number,
): string {
  return item?.Subtitle || `Step ${index + 1}`;
}

function getItemPaddedNumber(index: number): string {
  return String(index + 1).padStart(2, "0");
}

function getSectionTitleNode(props: WorkSectionProps): ReactNode {
  return props.setting.Title ?? undefined;
}

const generatedWorkRenderers: Record<string, GeneratedWorkRenderer> = {
  [WorkAppearance.Home2.key]: (props) => (
    <HomeTwoWorkSectionGenerated
      {...buildGeneratedSectionProps<HomeTwoWorkSectionGeneratedProps>(
        "HomeTwoWorkSection",
        {
          sectionDiv0Div0Div0Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "work",
          ),
          sectionDiv0Div0Div1Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div1Div0H20H2Text: getSectionTitleNode(props),
          sectionDiv0Div0Div1Div0H20Span0SpanText: renderHtmlValue(
            props.setting.Description,
          ),
          workSection6s: getListItems(props).map((item, index) => ({
            key: item.Id,
            alt: item.Title ?? `work-${index + 1}`,
            src:
              getItemVisual(item, "/Templates/Doccure/img/icons/search.svg") ??
              "/Templates/Doccure/img/icons/search.svg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [WorkAppearance.Home7.key]: (props) => (
    <HomeSevenWorkSectionGenerated
      {...buildGeneratedSectionProps<HomeSevenWorkSectionGeneratedProps>(
        "HomeSevenWorkSection",
        {
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text: getSectionTitleNode(props),
          sectionDiv0Div0Div0Div0H20Span0SpanText:
            props.setting.Description ?? undefined,
          workSection36FadeInUps: getListItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDuration: `${index + 1}s`,
            src:
              getItemVisual(item, "/Templates/Doccure/img/icons/work-01.svg") ??
              "/Templates/Doccure/img/icons/work-01.svg",
            div1Text: getItemPaddedNumber(index),
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [WorkAppearance.Home10.key]: (props) => {
    const items = getListItems(props);
    const collectionItems = items.slice(0, 3);
    const fourthItem = items[3];

    return (
      <HomeTenWorkSectionGenerated
        {...buildGeneratedSectionProps<HomeTenWorkSectionGeneratedProps>(
          "HomeTenWorkSection",
          {
            sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
            sectionDiv0Div0H20H2Text: getSectionTitleNode(props),
            workSection36s: collectionItems.map((item, index) => ({
              key: item.Id,
              className:
                index % 2 === 1
                  ? "work-item-ten work-two flex-fill"
                  : "work-item-ten flex-fill",
              div0Text: getItemPaddedNumber(index),
              src:
                getItemVisual(
                  item,
                  "/Templates/Doccure/img/icons/work-05.svg",
                ) ?? "/Templates/Doccure/img/icons/work-05.svg",
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              imageWithBasePath0ClassName:
                index % 2 === 0 ? "arrow-down-bg" : "arrow-up-bg",
              imageWithBasePath0Src:
                index % 2 === 0
                  ? "/Templates/Doccure/img/bg/arrow-down.png"
                  : "/Templates/Doccure/img/bg/arrow-up.png",
            })),
            sectionDiv0Div1Div3Div0Div1H30H3Text:
              fourthItem?.Title ?? undefined,
            sectionDiv0Div1Div3Div0Div0Div0ImageWithBasePath0ImageRenderer:
              fourthItem
                ? () => <Html value={fourthItem.Description ?? ""} />
                : undefined,
            sectionDiv0Div1Div3Div0Div1H30H3Renderer: fourthItem
              ? () => <h3 className="custom-title">{fourthItem.Title}</h3>
              : undefined,
          },
        )}
      />
    );
  },
  [WorkAppearance.Home11.key]: (props) => {
    const items = getListItems(props);
    const collectionItems = items.slice(0, 3);
    const fourthItem = items[3];

    return (
      <HomeElevenWorkSectionGenerated
        {...buildGeneratedSectionProps<HomeElevenWorkSectionGeneratedProps>(
          "HomeElevenWorkSection",
          {
            sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
            sectionDiv0Div0H20H2Text: getSectionTitleNode(props),
            workSection36s: collectionItems.map((item, index) => ({
              key: item.Id,
              className:
                [
                  "work-item-eleven wow fadeInUp",
                  "work-item-eleven two wow fadeInUp",
                  "work-item-eleven three wow fadeInUp",
                ][index] ?? "work-item-eleven wow fadeInUp",
              dataWowDuration: `${index + 1}s`,
              span0Text: getItemStepText(item, index),
              div0Text: renderHtmlValue(item.Description),
              src:
                getItemImageUrl(item.Image) ??
                "/Templates/Doccure/img/category/work-img-1.jpg",
              h30Text: item.Title ?? "",
            })),
            sectionDiv0Div1Div0Div3Div0Div0Span0SpanText:
              fourthItem?.Subtitle ?? getItemStepText(fourthItem, 3),
            sectionDiv0Div1Div0Div3Div0Div2Div0Div0DivText: renderHtmlValue(
              fourthItem?.Description,
            ),
            sectionDiv0Div1Div0Div3Div0Div2Div0ImageWithBasePath0ImageSrc:
              getItemImageUrl(fourthItem?.Image) ?? undefined,
            sectionDiv0Div1Div0Div3Div0Div2Div0ImageWithBasePath0ImageAlt:
              fourthItem?.Title ?? "work",
            sectionDiv0Div1Div0Div3Div0Div2H30H3Text:
              fourthItem?.Title ?? undefined,
          },
        )}
      />
    );
  },
  [WorkAppearance.Home12.key]: (props) => (
    <HomeTwelveWorkSectionGenerated
      {...buildGeneratedSectionProps<HomeTwelveWorkSectionGeneratedProps>(
        "HomeTwelveWorkSection",
        {
          sectionDiv0Div0H20H2Text: getSectionTitleNode(props),
          sectionDiv0Div0H20Span0SpanText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text2: "",
          sectionDiv0Div0P0PText: renderHtmlValue(props.setting.Description),
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "service",
          ),
          workSection366s: getListItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDuration: `${index + 1}s`,
            src:
              getItemVisual(
                item,
                "/Templates/Doccure/img/icons/work-icon-1.svg",
              ) ?? "/Templates/Doccure/img/icons/work-icon-1.svg",
            h30Text: item.Title ?? "",
            p0Text: item.Subtitle ?? getItemStepText(item, index),
            p1Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [WorkAppearance.Home14.key]: (props) => (
    <HomeFourteenTeamSectionGenerated
      {...buildGeneratedSectionProps<HomeFourteenTeamSectionGeneratedProps>(
        "HomeFourteenTeamSection",
        {
          sectionDiv0Div0H20H2Text: getSectionTitleNode(props),
          sectionDiv0Div0H20Span0SpanText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20Span1SpanText:
            props.setting.Description ?? undefined,
          teamSection36FadeInUps: getListItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDelay: `${0.2 + index * 0.2}s`,
            className:
              [
                "work-wrap flex-fill",
                "work-wrap work-02 flex-fill",
                "work-wrap work-03 flex-fill",
                "work-wrap work-04 flex-fill",
              ][index] ?? "work-wrap flex-fill",
            span1Text: getItemStepText(item, index),
            src:
              getItemImageUrl(item.Image) ??
              "/Templates/Doccure/img/service/work-01.jpg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [WorkAppearance.Home18.key]: (props) => (
    <HomeEighteenWorkSectionGenerated
      {...buildGeneratedSectionProps<HomeEighteenWorkSectionGeneratedProps>(
        "HomeEighteenWorkSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text: getSectionTitleNode(props),
          workSection366s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className:
              [
                "work-item-eighteen one",
                "work-item-eighteen two",
                "work-item-eighteen three",
                "work-item-eighteen four",
              ][index] ?? "work-item-eighteen one",
            h30Text: getItemPaddedNumber(index),
            span0Text: item.Subtitle ?? "",
            h30H30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            src:
              getItemImageUrl(item.Image) ??
              "/Templates/Doccure/img/bg/shadow-7.png",
          })),
        },
      )}
    />
  ),
};

const GeneratedWorkSection: FC<WorkSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedWorkRenderers,
    WorkAppearance.Home2.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedWorkSection;
